import formatCurrency from '@/utils/format-currency';
import { getLengthOfStayDiscount } from '@/utils/get-length-of-stay-discount';
import {
  BOOKING_AMOUNT_LABEL,
  STAY_FEES_COLLECTED_BY_RESORT_DISCLAIMER,
  STAY_FEES_COLLECTED_BY_RESORT_HELPER,
  STAY_FEES_COLLECTED_BY_RESORT_LABEL,
} from '@/utils/rate-totals';
import clsx from 'clsx';

import { PriceBreakdownDetailsModal } from './price-breakdown-details-modal';
import PriceBreakdownPopover, {
  PriceBreakdownItem,
} from './price-breakdown-popover';

export const PriceBreakdown: React.FC<{
  prices: PriceBreakdownItem[];
  price?: number;
  bookingAmount?: number;
  stayFees?: {
    amount: number;
  };
  hideNoValue?: boolean;
  priceIsBeforeTaxes?: boolean;
  checkIn?: string;
  checkOut?: string;
  thinBorder?: boolean;
  smallText?: boolean;
  totalLabel?: string;
}> = ({
  prices,
  price,
  bookingAmount,
  stayFees,
  hideNoValue,
  priceIsBeforeTaxes,
  checkIn,
  checkOut,
  thinBorder = false,
  smallText = false,
  totalLabel,
}) => {
  const pricebreakdownItems = hideNoValue
    ? prices.filter(
        ({ price, lineThroughPrice }) =>
          price !== 0 || (lineThroughPrice ?? 0) > 0
      )
    : prices;
  const hasSummaryBelow =
    typeof bookingAmount === 'number' && bookingAmount > 0;
  const borderClass = thinBorder ? 'border-gray-2' : 'border-gray-8';
  const borderWeight = thinBorder ? 'border-b' : 'border-b-2';
  const totalBorderClass = thinBorder
    ? 'border-t border-gray-2'
    : 'border-t-2 border-gray-8';
  const textClass = smallText ? 'text-sm' : undefined;

  return (
    <div className="flex w-full flex-col gap-y-2">
      <div className="flex w-full flex-col">
        {pricebreakdownItems.map((lineItem, index) => {
          const {
            label,
            price,
            details = [],
            lineThroughPrice = 0,
            labelClassName,
            priceClassName,
            useAccountingFormat = true,
            detailsPresentation = 'popover',
            variant = 'default',
          } = lineItem;
          const isLastLineItem = index === pricebreakdownItems.length - 1;
          const isSubtotal = variant === 'subtotal';
          const subtotalWeightClass = isSubtotal ? 'font-bold' : undefined;
          const tooltip =
            hideNoValue &&
            'tooltip' in lineItem &&
            typeof (lineItem as any).tooltip === 'string'
              ? (lineItem as any).tooltip
              : undefined;

          return (
            <div
              key={`${label}-price-breakdown-${index}`}
              className={clsx(
                'mt-2 flex flex-nowrap justify-between first:mt-0',
                isSubtotal && [totalBorderClass, 'pt-2 font-bold'],
                isLastLineItem &&
                  hasSummaryBelow && [borderWeight, borderClass, 'pb-2']
              )}
            >
              <div className="flex items-center">
                {details.length > 0 && detailsPresentation === 'modal' ? (
                  <PriceBreakdownDetailsModal
                    triggerLabel={label ?? ''}
                    title={label}
                    triggerClassName={clsx(
                      'whitespace-nowrap',
                      textClass,
                      subtotalWeightClass,
                      labelClassName
                    )}
                    details={details}
                    description={lineItem.detailsDescription}
                    total={price}
                    useAccountingFormat={useAccountingFormat}
                  />
                ) : details.length > 0 ? (
                  <div className="flex flex-row flex-nowrap items-center gap-1.5">
                    <span
                      className={clsx(
                        'whitespace-nowrap',
                        textClass,
                        subtotalWeightClass,
                        labelClassName
                      )}
                    >
                      {label}
                    </span>
                    <PriceBreakdownPopover
                      price={price}
                      details={details}
                      description={lineItem.detailsDescription}
                    />
                  </div>
                ) : (
                  <div className="flex items-center">
                    <span
                      className={clsx(
                        'whitespace-nowrap',
                        textClass,
                        subtotalWeightClass,
                        labelClassName
                      )}
                    >
                      {label}
                    </span>
                    {tooltip && (
                      <div className="has-tooltip relative ml-1.5">
                        <div className="flex cursor-pointer items-center text-gray-7 hover:text-black-0">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 256 256"
                          >
                            <path
                              fill="currentColor"
                              d="M140 176a4 4 0 0 1-4 4a12 12 0 0 1-12-12v-40a4 4 0 0 0-4-4a4 4 0 0 1 0-8a12 12 0 0 1 12 12v40a4 4 0 0 0 4 4a4 4 0 0 1 4 4m-16-84a8 8 0 1 0-8-8a8 8 0 0 0 8 8m104 36A100 100 0 1 1 128 28a100.11 100.11 0 0 1 100 100m-8 0a92 92 0 1 0-92 92a92.1 92.1 0 0 0 92-92"
                            />
                          </svg>
                        </div>
                        <div className="tooltip dark:bg-grey-600 -left-1.5 bottom-6 !z-50 min-w-[142px] rounded-xl bg-neutral-100 p-2 text-xs text-transparent">
                          <p className="break-normal text-xs text-gray-900 dark:text-white">
                            {tooltip}
                          </p>
                          <svg
                            className="dark:text-grey-600 absolute left-0 top-full ml-3 h-2 text-neutral-100"
                            x="0px"
                            y="0px"
                            viewBox="0 0 255 255"
                          >
                            <polygon
                              className="fill-current"
                              points="0,0 127.5,127.5 255,0"
                            />
                          </svg>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className={clsx('flex items-center text-end', textClass)}>
                {lineThroughPrice > 0 && (
                  <>
                    {checkIn && checkOut && (
                      <div className="group relative p-1">
                        <div className="dark:bg-grey-600 !pointer-events-none absolute -top-8 z-50 scale-0 rounded-lg bg-neutral-100 p-1.5 opacity-0 transition-all group-hover:scale-100 group-hover:opacity-100 group-focus:scale-100 group-focus:opacity-100 group-active:scale-100 group-active:opacity-100">
                          <p className="ut-xxs whitespace-nowrap break-normal text-gray-900 dark:text-white">
                            {getLengthOfStayDiscount(checkIn, checkOut)}
                          </p>
                          <svg
                            className="dark:text-grey-600 absolute left-1/2 top-full h-2 -translate-x-1/2 text-neutral-100"
                            x="0px"
                            y="0px"
                            viewBox="0 0 255 255"
                          >
                            <polygon
                              className="fill-current"
                              points="0,0 127.5,127.5 255,0"
                            />
                          </svg>
                        </div>

                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          className="text-gray-7 group-hover:text-black-0"
                          viewBox="0 0 256 256"
                        >
                          <path
                            fill="currentColor"
                            d="M140 176a4 4 0 0 1-4 4a12 12 0 0 1-12-12v-40a4 4 0 0 0-4-4a4 4 0 0 1 0-8a12 12 0 0 1 12 12v40a4 4 0 0 0 4 4a4 4 0 0 1 4 4m-16-84a8 8 0 1 0-8-8a8 8 0 0 0 8 8m104 36A100 100 0 1 1 128 28a100.11 100.11 0 0 1 100 100m-8 0a92 92 0 1 0-92 92a92.1 92.1 0 0 0 92-92"
                          />
                        </svg>
                      </div>
                    )}
                    {lineThroughPrice > 0 && (
                      <span
                        className={clsx(
                          'mr-2 text-gray-5 line-through',
                          textClass
                        )}
                      >
                        {formatCurrency(lineThroughPrice, true)}
                      </span>
                    )}
                  </>
                )}
                <span
                  className={clsx(
                    textClass,
                    subtotalWeightClass,
                    priceClassName
                  )}
                >
                  {formatCurrency(lineItem.price, true, useAccountingFormat)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      {typeof bookingAmount === 'number' && bookingAmount > 0 && (
        <div className="flex flex-col gap-y-3">
          <div className="flex w-full flex-row justify-between font-bold">
            <span className={clsx('text-inherit', textClass)}>
              {BOOKING_AMOUNT_LABEL}
            </span>
            <span className={clsx('text-end text-inherit', textClass)}>
              {formatCurrency(bookingAmount, true)}
            </span>
          </div>
          {stayFees && stayFees.amount > 0 && (
            <div className="grid grid-cols-3">
              <div className="col-span-2">
                <div className="flex flex-row flex-nowrap items-center gap-1.5">
                  <span className={clsx('whitespace-nowrap', textClass)}>
                    {STAY_FEES_COLLECTED_BY_RESORT_LABEL}
                  </span>
                  <PriceBreakdownPopover
                    description={STAY_FEES_COLLECTED_BY_RESORT_DISCLAIMER}
                    label=""
                  />
                </div>
                <p className="text-xs text-gray-5">
                  {STAY_FEES_COLLECTED_BY_RESORT_HELPER}
                </p>
              </div>
              <div className="text-end">
                <span className={textClass}>
                  {formatCurrency(stayFees.amount, true)}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
      {typeof price === 'number' && price > 0 && (
        <div
          className={clsx(
            'flex w-full flex-row justify-between',
            totalBorderClass,
            priceIsBeforeTaxes
              ? 'pt-2 font-bold'
              : 'pt-2 text-lg font-bold'
          )}
        >
          <span
            className={clsx(
              'text-inherit',
              textClass,
              priceIsBeforeTaxes && 'font-bold'
            )}
          >
            {totalLabel ??
              (priceIsBeforeTaxes ? 'Total before taxes' : 'Total')}
          </span>
          <span
            className={clsx(
              'text-end text-inherit',
              textClass,
              priceIsBeforeTaxes && 'font-bold'
            )}
          >
            {formatCurrency(price, true)}
          </span>
        </div>
      )}
    </div>
  );
};
